import { ILogger } from '../contracts/ILogger';
import { IRecordLogger } from '../contracts/IRecordLogger';
import { LogStyle } from '../enums/LogStyle';
import type { RecordLogCategory } from '../models/IRecordLogDTO';

/** ILogger implementation for console. */
export class ConsoleLogger implements ILogger {
  /** Singleton instance of ConsoleLogger. */
  private static instance: ConsoleLogger;

  /**
   * Factory method to get the singleton instance.
   *
   * @param logDebug
   * @returns The singleton Logger instance.
   */
  public static GetInstance(logDebug: boolean): ConsoleLogger {
    if (!ConsoleLogger.instance) {
      ConsoleLogger.instance = new ConsoleLogger(logDebug);
    }
    return ConsoleLogger.instance;
  }

  /** Optional sink that durably records `Warn`/`Error` calls. Installed at bootstrap via {@link SetRecorder}. */
  private recorder: IRecordLogger | null = null;

  /** Re-entry guard — set while forwarding to {@link recorder}, prevents recursion if the sink itself logs an error. */
  private recording: boolean = false;

  /**
   * Prevent external instantiation. Debug: (message: string, ...codes: LogStyle[]) => void;
   *
   * @param logDebug
   */
  private constructor(private logDebug: boolean) {}

  /**
   * Installs (or replaces) the durable recorder used by `Warn` and `Error`. Pass `null` to disable recording (e.g. during teardown).
   *
   * @param recorder - Sink implementation, or `null` to clear.
   */
  SetRecorder(recorder: IRecordLogger | null): void {
    this.recorder = recorder;
  }

  /**
   * @param text
   * @param codes
   * @returns The styled text.
   */
  GetStyledText(text: string, ...codes: LogStyle[]): string {
    return `${codes.join('')}${text}${LogStyle.Reset}`;
  }

  /**
   * Retrieves the error message from an unknown error object.
   *
   * @param error - The error object to extract the message from.
   * @param stack - Whether to return the stack trace if available.
   * @returns The error message as a string.
   */
  GetErrorMessage(error: unknown, stack: boolean = false): string {
    return error instanceof Error ? (stack ? `${error.message}\n${error.stack || ''}` : error.message) : String(error);
  }

  /**
   * Retrieves the error code from an unknown error object (e.g. Firebase AuthError).
   *
   * @param error - The error object to extract the code from.
   * @returns The error code string, or empty string if none.
   */
  GetErrorCode(error: unknown): string {
    return error instanceof Error && 'code' in error ? String((error as { code: unknown }).code) : '';
  }

  /**
   * Retrieves the error message from an unknown error object.
   *
   * @param error - The error object to extract the message from.
   * @returns The error message as a string.
   */
  GetErrorName(error: unknown): string {
    return error instanceof Error ? error.name : '';
  }

  /**
   * @param context - Source identifier (e.g. file/class name) prepended as `[context]`.
   * @param message - Log message body.
   * @param codes - Optional ANSI style codes.
   */
  Debug(context: string, message: string, ...codes: LogStyle[]): void {
    if (this.logDebug) {
      console.log(this.GetStyledText(`[${context}] ${message}`, ...codes));
    }
  }

  /**
   * @param context - Source identifier (e.g. file/class name) prepended as `[context]`.
   * @param message - Log message body.
   * @param codes - Optional ANSI style codes.
   */
  Log(context: string, message: string, ...codes: LogStyle[]): void {
    console.log(this.GetStyledText(`[${context}] ${message}`, ...codes));
    this.forwardToRecorder('log', context, message);
  }

  /**
   * @param context - Source identifier (e.g. file/class name) prepended as `[context]`.
   * @param message - Log message body.
   * @param codes - Optional ANSI style codes.
   */
  Error(context: string, message: string, ...codes: LogStyle[]): void {
    if (!codes.length) codes = [LogStyle.Red, LogStyle.Bold];
    console.error(this.GetStyledText(`[${context}] ${message}`, ...codes));
    this.forwardToRecorder('error', context, message);
  }

  /**
   * @param context - Source identifier (e.g. file/class name) prepended as `[context]`.
   * @param message - Log message body.
   * @param codes - Optional ANSI style codes.
   */
  Warn(context: string, message: string, ...codes: LogStyle[]): void {
    if (!codes.length) codes = [LogStyle.Yellow];
    console.warn(this.GetStyledText(`[${context}] ${message}`, ...codes));
    this.forwardToRecorder('warning', context, message);
  }

  /**
   * Console-only counterpart to {@link Error}. Skips the durable recorder so it is safe to call from inside the recorder pipeline (or its swallow paths) without recursion or extra DB writes.
   *
   * @param context - Source identifier (e.g. file/class name) prepended as `[context]`.
   * @param message - Log message body.
   * @param codes - Optional ANSI style codes.
   */
  ErrorLocal(context: string, message: string, ...codes: LogStyle[]): void {
    if (!codes.length) codes = [LogStyle.Red, LogStyle.Bold];
    console.error(this.GetStyledText(`[${context}] ${message}`, ...codes));
  }

  /**
   * Console-only counterpart to {@link Warn}. Skips the durable recorder so it is safe to call from inside the recorder pipeline (or its swallow paths) without recursion or extra DB writes.
   *
   * @param context - Source identifier (e.g. file/class name) prepended as `[context]`.
   * @param message - Log message body.
   * @param codes - Optional ANSI style codes.
   */
  WarnLocal(context: string, message: string, ...codes: LogStyle[]): void {
    if (!codes.length) codes = [LogStyle.Yellow];
    console.warn(this.GetStyledText(`[${context}] ${message}`, ...codes));
  }

  /**
   * Forwards a single warn/error call to the installed recorder. Guarded against re-entry — if the recorder itself logs, the inner call short-circuits.
   *
   * @param category - Severity classification.
   * @param origin - Source context string (file/class identifier).
   * @param text - Log message body.
   */
  private forwardToRecorder(category: RecordLogCategory, origin: string, text: string): void {
    if (!this.recorder || this.recording) return;
    this.recording = true;
    try {
      this.recorder.Record({ category, origin, text });
    } catch (err: unknown) {
      // Surface the failure to the console only — never re-enter the recorder. The durable record was already attempted; one missed durable write is preferable to recursion.
      this.ErrorLocal('ConsoleLogger', `recorder.Record threw: ${this.GetErrorMessage(err)}`);
    } finally {
      this.recording = false;
    }
  }
}
